import { useCallback, useMemo } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  FileText,
  ShieldCheck,
  TrendingUp,
  User,
} from "lucide-react";
import { BlockLink, SchoolLink } from "@/components/DrilldownLink";
import MistralGuidanceChip from "@/components/MistralGuidanceChip";
import HeroStatusChip from "@/components/HeroStatusChip";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import {
  buildAadhaarActionMarkdown,
  buildActionWorkstreams,
  buildBottomBlockLeaderboard,
  buildComplianceGapSummary,
  buildEscalationDecisionNote,
  buildExceptionMixBars,
  buildPrimaryExecutiveAction,
  buildPrioritySchoolQueue,
  buildSprintTimeline,
  buildOwnerClosureModel,
  buildEscalationRules,
  fmt,
  pct,
} from "@/utils/aadhaarDerived";
import "./AadhaarActionAccountabilityTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const WORKSTREAM_ICONS = {
  coverage: ShieldCheck,
  mismatch: AlertTriangle,
  mbu: User,
  governance: ClipboardList,
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const OWNER_ICONS = {
  red: AlertTriangle,
  purple: User,
  orange: FileText,
};

const ESCALATION_ICONS = {
  red: AlertTriangle,
  orange: TrendingUp,
  green: CheckCircle2,
};

export default function AadhaarActionAccountabilityTab({
  overview = {},
  blockData = [],
  bottomBlocks = [],
  highRiskSchools = [],
}) {
  const coverage = overview.aadhaar_coverage_pct ?? 0;
  const gapRemaining = Math.max(0, (overview.total_enrolment ?? 0) - (overview.aadhaar_passed ?? 0));
  const hasData = Boolean(overview?.total_enrolment);

  const buildLocalMarkdown = useCallback(
    () => buildAadhaarActionMarkdown(overview, blockData, bottomBlocks, highRiskSchools),
    [overview, blockData, bottomBlocks, highRiskSchools]
  );

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        c: overview?.aadhaar_coverage_pct,
        r: highRiskSchools?.length,
        b: blockData?.length,
      }),
    [overview?.aadhaar_coverage_pct, highRiskSchools?.length, blockData?.length]
  );

  const {
    loading: aiLoading,
    pickPrimaryAction,
    pickFromBucket,
    provider,
    isMistral,
  } = useMistralNarrativeOverlay({
    kind: "aadhaar-action",
    title: "Aadhaar Accountability Control Center",
    buildLocalMarkdown,
    context: { overview, blockData, bottomBlocks, highRiskSchools },
    enabled: hasData,
    scopeKey,
  });

  const localPrimaryAction = useMemo(
    () => buildPrimaryExecutiveAction(blockData),
    [blockData]
  );
  const primaryAction = useMemo(
    () => (aiLoading ? localPrimaryAction : pickPrimaryAction(localPrimaryAction)),
    [aiLoading, pickPrimaryAction, localPrimaryAction]
  );
  const workstreams = useMemo(() => buildActionWorkstreams(overview), [overview]);
  const leaderboard = useMemo(
    () => buildBottomBlockLeaderboard(blockData, bottomBlocks, 10),
    [blockData, bottomBlocks]
  );
  const gapSummary = useMemo(() => buildComplianceGapSummary(overview), [overview]);
  const exceptionMix = useMemo(() => buildExceptionMixBars(blockData, 6), [blockData]);
  const schoolQueue = useMemo(() => buildPrioritySchoolQueue(highRiskSchools, 20), [highRiskSchools]);
  const localDecisionNote = useMemo(
    () => buildEscalationDecisionNote(blockData, bottomBlocks),
    [blockData, bottomBlocks]
  );
  const decisionNote = useMemo(
    () => (aiLoading ? localDecisionNote : pickFromBucket(localDecisionNote, "recs", 0) || localDecisionNote),
    [aiLoading, pickFromBucket, localDecisionNote]
  );
  const sprintTimeline = useMemo(
    () => buildSprintTimeline(overview, bottomBlocks, highRiskSchools),
    [overview, bottomBlocks, highRiskSchools]
  );
  const ownerClosureModel = useMemo(() => buildOwnerClosureModel(overview), [overview]);
  const escalationRules = useMemo(
    () => buildEscalationRules(overview, highRiskSchools),
    [overview, highRiskSchools]
  );

  const gaugeStyle = {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--aa-success) 0 ${coverage}%, rgba(16, 185, 129, 0.13) ${coverage}% 100%)`,
  };

  return (
    <div className="aa-action-tab">
      <section className="aa-action-hero" id="aa-action-center">
        <div className="aa-action-layout">
          <div>
            <div className="aa-action-main">
              <div
                className="aa-action-gauge"
                style={gaugeStyle}
                aria-label={`Aadhaar coverage ${pct(coverage)}`}
              >
                <div className="aa-action-gauge-inner">
                  <strong>{pct(coverage)}</strong>
                  <span>Coverage</span>
                </div>
              </div>
              <div className="aa-action-copy">
                <h2 className="dashboard-kpi-hero-title">Accountability Control Center</h2>
                <p>
                  Coverage is strong, but the remaining gap needs a focused ownership model across
                  blocks, schools, operators and review officers.
                </p>
                <div className="aa-action-status-row">
                  {isMistral && !aiLoading ? (
                    <HeroStatusChip chipClass="aa-action-status-chip" variant="purple" showChevron={false}>
                      Mistral AI Assisted
                    </HeroStatusChip>
                  ) : (
                    <MistralGuidanceChip provider={provider} loading={aiLoading} />
                  )}
                  <HeroStatusChip chipClass="aa-action-status-chip" variant="green" showChevron={false}>
                    On-track Coverage
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="aa-action-status-chip"
                    variant="red"
                    onClick={() => scrollToId("aa-action-school-table")}
                    ariaLabel="Name mismatch priority — view priority school queue"
                  >
                    Name Mismatch Priority
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="aa-action-status-chip"
                    onClick={gapRemaining > 0 ? () => scrollToId("aa-action-sprint") : undefined}
                    ariaLabel={
                      gapRemaining > 0 ? `${fmt(gapRemaining)} gap to 100% — view closure sprint plan` : undefined
                    }
                  >
                    {fmt(gapRemaining)} Gap to 100%
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="aa-action-summary-note">
              <div className="aa-action-icon-box red">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="aa-action-metric-grid" aria-label="Action KPIs">
            <article className="aa-action-metric-card green">
              <small>Primary KPI</small>
              <strong>{pct(coverage)}</strong>
              <span>Aadhaar Coverage</span>
            </article>
            <article className="aa-action-metric-card red">
              <small>Highest Risk</small>
              <strong>{pct(overview.name_mismatch_pct ?? 0)}</strong>
              <span>Name Mismatch</span>
            </article>
            <article className="aa-action-metric-card purple">
              <small>Operational Load</small>
              <strong>{pct(overview.mbu_pending_total_pct ?? 0)}</strong>
              <span>MBU Pending</span>
            </article>
            <article className="aa-action-metric-card orange">
              <small>Schools at Risk</small>
              <strong>{overview.high_risk_schools ?? 0}</strong>
              <span>Exception &gt; 10%</span>
            </article>
          </div>
        </div>
      </section>

      <div className="aa-action-section-title">
        <div>
          <h2>Action Workstreams</h2>
          <p>
            Four focused workstreams replace raw KPI cards with clear closure targets, owner type
            and action priority.
          </p>
        </div>
        <button type="button" className="aa-action-section-action" onClick={() => scrollToId("aa-action-sprint")}>
          View 7-day sprint →
        </button>
      </div>

      <section className="aa-action-workstream-grid">
        {workstreams.map((card) => {
          const Icon = WORKSTREAM_ICONS[card.id] || ClipboardList;
          return (
            <article key={card.id} className="aa-action-work-card">
              <div className="aa-action-work-head">
                <div className={`aa-action-icon-box ${card.iconClass}`}>
                  <Icon size={22} />
                </div>
                <span className={`aa-action-priority ${card.priorityClass}`.trim()}>{card.priority}</span>
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <div className="aa-action-work-value">
                <strong>{card.value}</strong>
                <span>{card.unit}</span>
              </div>
              <div className={`aa-action-progress-line ${card.barClass || ""}`.trim()}>
                <i style={{ width: `${card.barWidth}%` }} />
              </div>
              <div className="aa-action-work-meta">
                <span>Owner: {card.owner}</span>
                <span>{card.cadence}</span>
              </div>
            </article>
          );
        })}
      </section>

      <div className="aa-action-section-title" id="aa-action-diagnostics">
        <div>
          <h2>Block Accountability Diagnostics</h2>
          <p>
            Keep the leaderboard, but make it actionable by showing bottom blocks, exception mix and
            the exact district gap to 100% coverage.
          </p>
        </div>
        <button type="button" className="aa-action-section-action" onClick={() => scrollToId("aa-action-school-table")}>
          School queue →
        </button>
      </div>

      <section className="aa-action-analytics-grid">
        <article className="aa-action-panel">
          <div className="aa-action-panel-head">
            <div>
              <h3>Bottom 10 Blocks Requiring Review</h3>
              <p>Prioritize blocks with lower coverage and higher exception burden.</p>
            </div>
            <span className="aa-action-pill">Lowest coverage</span>
          </div>
          <div className="aa-action-leader-list">
            {leaderboard.map((row) => (
              <div key={row.block} className={`aa-action-leader-row ${row.risk ? "risk" : ""}`.trim()}>
                <div className="aa-action-rank-badge">{row.rank}</div>
                <div className="aa-action-leader-main">
                  <b>{row.block}</b>
                  <span>
                    {row.schools.toLocaleString("en-IN")} schools · {row.students.toLocaleString("en-IN")}{" "}
                    students · {pct(row.coverage)} coverage
                  </span>
                </div>
                <div className={`aa-action-score-pill ${row.risk ? "red" : ""}`.trim()}>
                  {row.gapLabel} gap
                </div>
              </div>
            ))}
          </div>
        </article>

        <article className="aa-action-panel">
          <div className="aa-action-panel-head">
            <div>
              <h3>Compliance Gap Analysis</h3>
              <p>Students remaining to reach 100% Aadhaar coverage.</p>
            </div>
            <span className="aa-action-pill">Target 100%</span>
          </div>
          <div className="aa-action-gap-card">
            <div className="aa-action-gap-hero">
              <span>Gap to 100% Coverage</span>
              <strong>{gapSummary.remainingLabel}</strong>
              <span>students remaining</span>
            </div>
            <div>
              <div className="aa-action-target-labels">
                <span>Current Coverage</span>
                <b>{gapSummary.coverageLabel}</b>
              </div>
              <div className="aa-action-target-track">
                <i style={{ width: `${gapSummary.coverage}%` }} />
              </div>
              <div className="aa-action-target-labels">
                <span>{gapSummary.coveredLabel} covered</span>
                <span>Target: 100%</span>
              </div>
            </div>
            <div className="aa-action-issue-grid">
              <div className="aa-action-issue-box red">
                <strong>{fmt(gapSummary.failed)}</strong>
                <span>Failed</span>
              </div>
              <div className="aa-action-issue-box orange">
                <strong>{fmt(gapSummary.pending)}</strong>
                <span>Pending</span>
              </div>
              <div className="aa-action-issue-box gray">
                <strong>{fmt(gapSummary.notProvided)}</strong>
                <span>Not Provided</span>
              </div>
            </div>
          </div>
        </article>
      </section>

      <section className="aa-action-analytics-grid">
        <article className="aa-action-panel">
          <div className="aa-action-panel-head">
            <div>
              <h3>Exception Mix by Priority Block</h3>
              <p>
                Use this to decide whether a block needs data correction, parent outreach or MBU
                clearance.
              </p>
            </div>
            <span className="aa-action-pill">Failed · Pending · MBU</span>
          </div>
          <div className="aa-action-bar-stack">
            {exceptionMix.map((row) => (
              <div key={row.block} className="aa-action-stack-row">
                <label>{row.block}</label>
                <div className="aa-action-stack-track">
                  <span className="aa-action-seg fail" style={{ width: `${row.failedPct}%` }} />
                  <span className="aa-action-seg pending" style={{ width: `${row.pendingPct}%` }} />
                  <span className="aa-action-seg mbu" style={{ width: `${row.mbuPct}%` }} />
                </div>
                <b>{row.priority}</b>
              </div>
            ))}
          </div>
          <div className="aa-action-legend">
            <span>
              <i className="aa-action-dot" style={{ background: "var(--aa-danger)" }} />
              Failed
            </span>
            <span>
              <i className="aa-action-dot" style={{ background: "var(--aa-secondary)" }} />
              Pending
            </span>
            <span>
              <i className="aa-action-dot" style={{ background: "var(--aa-purple)" }} />
              MBU Pending
            </span>
          </div>
        </article>

        <article className="aa-action-panel">
          <div className="aa-action-panel-head">
            <div>
              <h3>Owner-Wise Closure Model</h3>
              <p>Each issue type has a clear owner, action, SLA and escalation path.</p>
            </div>
            <span className="aa-action-pill">Accountability</span>
          </div>
          <div className="aa-action-accountability-card">
            {ownerClosureModel.map((item) => {
              const Icon = OWNER_ICONS[item.iconClass] || AlertTriangle;
              return (
              <div key={item.title} className="aa-action-action-item">
                <div className={`aa-action-icon-box ${item.iconClass}`}>
                  <Icon size={22} />
                </div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.body}</p>
                </div>
              </div>
              );
            })}
            <div className="aa-action-action-footer">
              <b>Review cadence</b>
              <span>
                Daily school updates, every 48-hour block review, weekly district escalation for
                unresolved high-risk records.
              </span>
            </div>
          </div>
        </article>
      </section>

      <div className="aa-action-section-title" id="aa-action-sprint">
        <div>
          <h2>7-Day Aadhaar Closure Sprint</h2>
          <p>
            A simple execution timeline helps district users move from dashboard viewing to
            disciplined follow-through.
          </p>
        </div>
        <button type="button" className="aa-action-section-action" onClick={() => scrollToId("aa-action-school-table")}>
          Open school queue ↓
        </button>
      </div>

      <section className="aa-action-two-column">
        <article className="aa-action-panel">
          <div className="aa-action-panel-head">
            <div>
              <h3>Execution Timeline</h3>
              <p>Recommended sequence for bottom-block improvement.</p>
            </div>
            <span className="aa-action-pill">Sprint plan</span>
          </div>
          <div className="aa-action-sprint-list">
            {sprintTimeline.map((step) => (
              <div key={step.day} className="aa-action-sprint-item">
                <div className="aa-action-sprint-day">{step.day}</div>
                <div>
                  <h4>{step.title}</h4>
                  <p>{step.body}</p>
                </div>
                <span className="aa-action-owner-chip">{step.owner}</span>
              </div>
            ))}
          </div>
        </article>

        <article className="aa-action-panel">
          <div className="aa-action-panel-head">
            <div>
              <h3>Escalation Rules</h3>
              <p>Use traffic-light rules for action ownership and follow-up priority.</p>
            </div>
            <span className="aa-action-pill">SLA</span>
          </div>
          <div className="aa-action-accountability-card">
            {escalationRules.map((item) => {
              const Icon = ESCALATION_ICONS[item.iconClass] || AlertTriangle;
              return (
                <div key={item.title} className="aa-action-action-item">
                  <div className={`aa-action-icon-box ${item.iconClass}`}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
            <div className="aa-action-action-footer">
              <b>Decision note</b>
              <span>{decisionNote}</span>
            </div>
          </div>
        </article>
      </section>

      <section className="aa-action-panel aa-action-table-card" id="aa-action-school-table">
        <div className="aa-action-table-head">
          <div>
            <h3>Priority School Action Queue</h3>
            <p>High-risk schools are converted into owner-based tasks with recommended next action.</p>
          </div>
          <span className="aa-action-pill">Top priority</span>
        </div>
        <div className="aa-action-table-wrap">
          <table className="aa-action-table" aria-label="Priority school action queue">
            <thead>
              <tr>
                <th>Rank</th>
                <th>School</th>
                <th>Block</th>
                <th className="right">Enrolment</th>
                <th className="right">Exception Rate</th>
                <th className="right">Coverage</th>
                <th>Owner</th>
                <th>Next Action</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {schoolQueue.map((row) => (
                <tr key={`${row.udiseCode || row.school}-${row.rank}`}>
                  <td>
                    <span className="aa-action-rank-dot">{row.rank}</span>
                  </td>
                  <td>
                    {row.udiseCode ? (
                      <SchoolLink udiseCode={row.udiseCode} className="aa-action-school-link">
                        {row.school}
                      </SchoolLink>
                    ) : (
                      <span className="aa-action-school-link">{row.school}</span>
                    )}
                  </td>
                  <td>
                    {row.blockCode ? (
                      <BlockLink blockCode={row.blockCode}>{row.block}</BlockLink>
                    ) : (
                      row.block
                    )}
                  </td>
                  <td className="right">{row.enrolment.toLocaleString("en-IN")}</td>
                  <td className="right">
                    <span className={`aa-action-tag ${row.exceptionTone}`.trim()}>{row.exceptionRate}</span>
                  </td>
                  <td className="right">{row.coverage}</td>
                  <td>{row.owner}</td>
                  <td>{row.nextAction}</td>
                  <td>
                    <span className={`aa-action-tag ${row.statusTone}`.trim()}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
